import React, { useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Input, Textarea, Button, Select, Table } from '@crisellevaldez/speculo-ui';
import { ScrollableCell } from '../../../common/ScrollableCell';
import { useQueryClient } from '@tanstack/react-query';
import { resolveHostname } from '../../../../api/storefront';
import {
  CreateTenantData,
  CreateTenantRequest,
  createTenantSchema,
} from '../../../../schemas/tenant.schema';
import { tenantManagementApi } from '../../../../api/tenantManagement';
import { generateCitySitemap, subscribeToMLS } from '../../../../api/cities';
import { StickyNote, Palette, BarChart, MapPin, Trash } from 'lucide-react';
import { IconPill } from '../../../common/IconPill';
import { CitySelector } from './CitySelector';
import { useOrganizationLogo } from '../../../../hooks/organizations/useOrganizationLogo';
import { useOrganizationFavicon } from '../../../../hooks/organizations/useOrganizationFavicon';
import { composeLinkUrl, LinkScheme, LINK_SCHEME_OPTIONS } from '../../../../utils/urlUtils';

interface CreateWebsiteFormProps {
  organizationId: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
  onSubmitting: (isSubmitting: boolean) => void;
}

export const CreateWebsiteForm: React.FC<CreateWebsiteFormProps> = ({
  organizationId,
  onSuccess,
  onError,
  onSubmitting,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFavicon, setSelectedFavicon] = useState<File | null>(null);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedMLS, setSelectedMLS] = useState<string[]>([]);
  const [logoSize, setLogoSize] = useState<'small' | 'medium' | 'large'>('small');
  const [logoPlacement, setLogoPlacement] = useState<'header' | 'both'>('header');
  const [orgLinks, setOrgLinks] = useState<{ key: string; value: string }[]>([]);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkScheme, setNewLinkScheme] = useState<LinkScheme>('https://');

  const { uploadLogo } = useOrganizationLogo();
  const { uploadFavicon } = useOrganizationFavicon();

  const logoSizes = {
    small: { width: 100, height: 50 },
    medium: { width: 200, height: 100 },
    large: { width: 260, height: 130 },
  };

  const handleAddLink = () => {
    if (newLinkTitle && newLinkUrl) {
      const newLinks = [
        ...orgLinks,
        {
          key: newLinkTitle,
          value: composeLinkUrl(newLinkScheme, newLinkUrl),
        },
      ];
      setOrgLinks(newLinks);
      setNewLinkTitle('');
      setNewLinkUrl('');
    }
  };

  const handleDeleteLink = useCallback((linkKey: string) => {
    setOrgLinks((prevLinks) => prevLinks.filter((link) => link.key !== linkKey));
  }, []);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<CreateTenantData>({
    resolver: zodResolver(createTenantSchema),
    defaultValues: {
      id: organizationId,
      name: '',
      domain: {
        primary: '',
        aliases: [],
      },
      analytics: {
        googleTag: '',
        gtmHead: '',
        gtmBody: '',
        gaHead: '',
      },
      branding: {
        colors: {
          primary: '#000000',
          secondary: '#000000',
          accent: '#000000',
          background: '#FFFFFF',
          text: '#000000',
        },
        logo: {
          url: '',
          width: 150,
          height: 50,
          placement: 'header',
        },
        fonts: {
          primary: 'Poppins',
          secondary: 'Poppins',
        },
      },
    },
    mode: 'onSubmit',
  });

  const queryClient = useQueryClient();

  const onSubmit = async (data: CreateTenantData) => {
    if (!selectedFile) {
      onError(new Error('Logo is required'));
      return;
    }

    onSubmitting(true);
    try {
      // Step 1: Check the domain isn't already taken (via Storefront /resolve, Spanner)
      // BEFORE any uploads, to avoid wasting resources.
      if (data.domain.primary) {
        const res = await resolveHostname(data.domain.primary);
        if (res.exists && res.tenantId !== organizationId) {
          throw new Error(
            `Domain "${data.domain.primary}" is already registered to another organization.`
          );
        }
      }

      // Step 2: Register domain in Firebase Auth and Hosting FIRST
      // This should be done before uploading assets to avoid wasting resources if it fails
      if (data.domain.primary) {
        try {
          const domainResult = await tenantManagementApi.registerDomain({
            organisation_id: organizationId,
            domain: data.domain.primary,
          });
          console.log('Domain registration result:', domainResult.data);
        } catch (domainError) {
          console.error('Domain registration error:', domainError);
          throw new Error(
            `Failed to register domain: ${domainError instanceof Error ? domainError.message : 'Unknown error'}`
          );
        }
      }

      // Step 3: Upload logo and favicon (only after domain checks pass)
      const [logoUrl, faviconUrl] = await Promise.all([
        selectedFile ? uploadLogo(selectedFile, organizationId, '') : Promise.resolve(''),
        selectedFavicon ? uploadFavicon(selectedFavicon, organizationId, '') : Promise.resolve(''),
      ]);

      // Step 4: Create new tenant with org ID and basic analytics
      const createTenantData: CreateTenantRequest = {
        id: data.id,
        name: data.name,
        domain: data.domain,
        analytics: {},
        branding: {
          ...data.branding,
          colors: {
            ...data.branding.colors,
            accent: data.branding.colors.primary, // Set accent to match primary color
          },
          logo: {
            url: logoUrl,
            width: logoSizes[logoSize].width,
            height: logoSizes[logoSize].height,
            placement: logoPlacement,
          },
          favicon: faviconUrl
            ? {
                url: faviconUrl,
                width: 32,
                height: 32,
              }
            : undefined,
        },
      };

      // Step 5: Create tenant with org links - only called if domain registration succeeded
      const tenantResponse = await tenantManagementApi.createTenant(createTenantData, orgLinks);

      // Subscribe to selected cities if any are selected
      if (selectedCities.length > 0) {
        await tenantManagementApi.subscribeToCity({
          tenant_id: organizationId,
          city_ids: selectedCities,
        });

        // Generate sitemap after successful subscription
        await generateCitySitemap(organizationId);
      }

      // Analytics is persisted by the storefront create path (Spanner
      // analytics_* columns). The old analyticsSetup Cloud Function wrote
      // decommissioned Firestore and, being awaited, would fail an
      // otherwise-successful create.

      // MLS functionality commented out as requested
      /*
      // Subscribe to MLS
      if (selectedMLS.length > 0) {
        await subscribeToMLS(organizationId, selectedMLS);
      }
      */

      // Invalidate queries to force a refetch
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tenant', organizationId] }),
        queryClient.invalidateQueries({ queryKey: ['organization', organizationId] }),
      ]);
      onSuccess();
    } catch (error) {
      onError(error instanceof Error ? error : new Error('An error occurred'));
    } finally {
      onSubmitting(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file) {
      setValue('branding.logo.url', URL.createObjectURL(file)); // Set the actual object URL to pass validation
      setValue('branding.logo.width', logoSizes[logoSize].width);
      setValue('branding.logo.height', logoSizes[logoSize].height);
    } else {
      setValue('branding.logo.url', '');
    }
  };

  const handleLogoSizeChange = (value: string | string[]) => {
    if (typeof value === 'string') {
      const newSize = value as 'small' | 'medium' | 'large';
      setLogoSize(newSize);
      if (selectedFile) {
        setValue('branding.logo.width', logoSizes[newSize].width);
        setValue('branding.logo.height', logoSizes[newSize].height);
      }
    }
  };

  return (
    <form
      id="create-website-form"
      onSubmit={handleSubmit(
        async (data) => {
          return onSubmit(data);
        },
        (errors) => {
          console.error('Form validation errors:', errors);
          return false;
        }
      )}
      className="space-y-6"
      noValidate
    >
      <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
        <div className="flex items-center gap-4 mb-3">
          <IconPill icon={StickyNote} size="lg" />
          <h4 className="text-sm font-bold text-foreground">IDX Site Setup</h4>
        </div>

        <hr className="dark:border-white/10" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <Form.Control required>
            <Form.Label>Website/Team Name</Form.Label>
            <Form.HelperText>Display name for the site</Form.HelperText>
            <Input {...register('name')} error={errors.name?.message} placeholder="Example Corp" />
          </Form.Control>

          <Form.Control required>
            <Form.Label>Primary Domain</Form.Label>
            <Form.HelperText>Main domain for the site</Form.HelperText>
            <Input
              {...register('domain.primary')}
              error={errors.domain?.primary?.message}
              placeholder="search.example.com"
            />
          </Form.Control>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
        <div className="flex items-center gap-4 mb-3">
          <IconPill icon={Palette} size="lg" />
          <h4 className="text-sm font-bold text-foreground">Branding & Styles</h4>
        </div>

        <hr className="dark:border-white/10" />
        <div className="space-y-6 mt-5">
          {/* Logo and Favicon Upload */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Form.Control error={errors.branding?.logo?.url?.message}>
              <Form.Label>
                Logo <span className="text-red-500">*</span>
              </Form.Label>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('logo-upload')?.click()}
                  >
                    Browse
                  </Button>
                  <Select
                    value={logoSize}
                    onChange={handleLogoSizeChange}
                    options={[
                      { value: 'small', label: 'Small' },
                      { value: 'medium', label: 'Medium ' },
                      { value: 'large', label: 'Large' },
                    ]}
                  />
                  <Select
                    value={logoPlacement}
                    onChange={(value) => setLogoPlacement(value as 'header' | 'both')}
                    options={[
                      { value: 'header', label: 'Header Only' },
                      { value: 'both', label: 'Header & Footer' },
                    ]}
                  />
                </div>
                {isSubmitted && !selectedFile && (
                  <span className="text-red-500 text-sm">Logo is required</span>
                )}
                {selectedFile && (
                  <div className="flex justify-start">
                    <div
                      className="rounded-md overflow-hidden bg-gray-50 dark:bg-white/5"
                      style={{
                        width: logoSizes[logoSize].width,
                        height: logoSizes[logoSize].height,
                      }}
                    >
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="Logo preview"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
              <input
                id="logo-upload"
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleLogoChange}
              />
            </Form.Control>

            <Form.Control>
              <Form.Label>Favicon</Form.Label>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('favicon-upload')?.click()}
                >
                  Browse
                </Button>
                {selectedFavicon && (
                  <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-50 dark:bg-white/5">
                    <img
                      src={URL.createObjectURL(selectedFavicon)}
                      alt="Favicon preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
              <input
                id="favicon-upload"
                type="file"
                className="hidden"
                accept="image/x-icon,image/png"
                onChange={(e) => setSelectedFavicon(e.target.files?.[0] || null)}
              />
            </Form.Control>
          </div>

          <hr className="mb-5 dark:border-white/10" />

          {/* Colors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Controller
              name="branding.colors.primary"
              control={control}
              render={({ field }) => (
                <Form.Control>
                  <Form.Label>
                    Primary Color <span className="text-red-500">*</span>
                  </Form.Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="h-8 w-8 rounded-md cursor-pointer border border-gray-200 dark:border-white/10 flex-shrink-0"
                    />
                    <Input
                      {...field}
                      className="uppercase"
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    />
                  </div>
                </Form.Control>
              )}
            />
            <Controller
              name="branding.colors.secondary"
              control={control}
              render={({ field }) => (
                <Form.Control>
                  <Form.Label>
                    Secondary Color <span className="text-red-500">*</span>
                  </Form.Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      className="h-8 w-8 rounded-md cursor-pointer border border-gray-200 dark:border-white/10 flex-shrink-0"
                    />
                    <Input
                      {...field}
                      className="uppercase"
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    />
                  </div>
                </Form.Control>
              )}
            />
          </div>

          <hr className="mt-8 mb-5 dark:border-white/10" />

          {/* Compliance Links */}
          <div className="mt-6">
            <Form.Label>Compliance Links</Form.Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Compliance links appear at the footer of your page.
            </p>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-[1fr,minmax(120px,auto),2fr,auto] gap-4">
                <Input
                  placeholder="Enter title"
                  value={newLinkTitle}
                  onChange={(e) => setNewLinkTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newLinkTitle && newLinkUrl) {
                        handleAddLink();
                      }
                    }
                  }}
                />
                <Select
                  options={LINK_SCHEME_OPTIONS.map(({ value, label }) => ({ value, label }))}
                  value={newLinkScheme}
                  onChange={(value) => setNewLinkScheme(value as LinkScheme)}
                />
                <div className="relative w-full">
                  <Input
                    placeholder={
                      LINK_SCHEME_OPTIONS.find((option) => option.value === newLinkScheme)
                        ?.placeholder ?? 'example.com'
                    }
                    className="w-full"
                    value={newLinkUrl}
                    onChange={(e) => setNewLinkUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (newLinkTitle && newLinkUrl) {
                          handleAddLink();
                        }
                      }
                    }}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setNewLinkTitle('');
                      setNewLinkUrl('');
                      setNewLinkScheme('https://');
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleAddLink}
                    disabled={!newLinkTitle || !newLinkUrl}
                  >
                    Add Link
                  </Button>
                </div>
              </div>

              <div className="rounded-md">
                <div className="overflow-auto">
                  {Array.isArray(orgLinks) && orgLinks.length > 0 ? (
                    <Table
                      columns={[
                        {
                          key: 'title',
                          header: 'Title',
                          width: '300px',
                          resizable: true,
                          cell: (item) => item.key,
                        },
                        {
                          key: 'url',
                          header: 'URL',
                          width: '300px',
                          cell: (item) => (
                            <ScrollableCell>
                              <span>{item.value}</span>
                            </ScrollableCell>
                          ),
                        },
                        {
                          key: 'actions',
                          header: 'Actions',
                          width: '100px',
                          isCentered: true,
                          cell: (item) => (
                            <div className="flex items-center gap-2 justify-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteLink(item.key)}
                                className="text-white hover:bg-red-600 bg-red-700 border-0"
                                style={{ padding: '10px' }}
                              >
                                <Trash className="w-4 h-4" />
                              </Button>
                            </div>
                          ),
                        },
                      ]}
                      data={orgLinks}
                      keyExtractor={(item) => item.key}
                    />
                  ) : (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                      No compliance links added yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
        <div className="flex items-center gap-4 mb-3">
          <IconPill icon={BarChart} size="lg" />
          <h4 className="text-sm font-bold text-foreground">Analytics Configuration</h4>
        </div>

        <hr className="dark:border-white/10" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <Form.Control>
            <Form.Label>Google Tag</Form.Label>
            <Textarea
              {...register('analytics.googleTag')}
              error={errors.analytics?.googleTag?.message}
              placeholder="Enter Google Tag script"
              rows={3}
            />
          </Form.Control>
          <Form.Control>
            <Form.Label>GTM Head</Form.Label>
            <Textarea
              {...register('analytics.gtmHead')}
              error={errors.analytics?.gtmHead?.message}
              placeholder="Enter GTM Head script"
              rows={3}
            />
          </Form.Control>
          <Form.Control>
            <Form.Label>GTM Body</Form.Label>
            <Textarea
              {...register('analytics.gtmBody')}
              error={errors.analytics?.gtmBody?.message}
              placeholder="Enter GTM Body script"
              rows={3}
            />
          </Form.Control>
          <Form.Control>
            <Form.Label>GA Head</Form.Label>
            <Textarea
              {...register('analytics.gaHead')}
              error={errors.analytics?.gaHead?.message}
              placeholder="Enter GA Head script"
              rows={3}
            />
          </Form.Control>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
        <div className="flex items-center gap-4 mb-3">
          <IconPill icon={MapPin} size="lg" />
          <h4 className="text-sm font-bold text-foreground">City & MLS Management</h4>
        </div>

        <hr className="dark:border-white/10" />
        <div className="mt-5 space-y-6">
          <div>
            <CitySelector
              organizationId={organizationId}
              selectedCities={selectedCities}
              onChange={setSelectedCities}
            />
          </div>
          {/* MLS functionality commented out as requested
          <div>
            <Form.Label>Subscribe MLS</Form.Label>
            <div className="mt-2 border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex flex-col gap-4">
                <div className="text-sm text-gray-500">Add MLS providers to subscribe to</div>
                <Input
                  placeholder="Type a tag and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const value = e.currentTarget.value.trim();
                      if (value) {
                        // Check if MLS already exists (case insensitive)
                        const exists = selectedMLS.some(
                          (mls) => mls.toLowerCase() === value.toLowerCase()
                        );
                        if (!exists) {
                          setSelectedMLS([...selectedMLS, value]);
                          e.currentTarget.value = '';
                        }
                      }
                    }
                  }}
                />
                {selectedMLS.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedMLS.map((mls) => (
                      <div
                        key={mls}
                        className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md"
                      >
                        <span className="text-sm">{mls}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMLS(selectedMLS.filter((m) => m !== mls));
                          }}
                          className="text-primary hover:text-primary/80"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          */}
        </div>
      </div>
    </form>
  );
};
